import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { consignmentApi, type SlaCompliance } from '@/api/services';
import { cn } from '@/lib/utils';

/**
 * How the arrangement is performing, against what was agreed.
 *
 * Four verdicts and they are not four shades of the same thing. Met and missed
 * are measurements. **No target** means nobody agreed one, so there is nothing to
 * comply with. **Not measured** means no day of position has been recorded, so
 * the question cannot be answered at all — which is a different problem from
 * failing, and drawing them the same way would hide a scheduler that had stopped
 * running.
 *
 * The window is stated on every figure, in days actually recorded rather than
 * days elapsed. A compliance number over three days is not the same claim as one
 * over ninety, and printing both as a bare percentage would make them look it.
 */

const VERDICT: Record<SlaCompliance['verdict'], { label: string; tone: string; blurb: string }> = {
  MET: {
    label: 'Meeting the target',
    tone: 'bg-status-success-subtle text-status-success-foreground',
    blurb: 'Availability is at or above what was agreed.',
  },
  MISSED: {
    label: 'Below target',
    tone: 'bg-status-destructive-subtle text-status-destructive-foreground',
    blurb: 'Availability is under what was agreed for this contract.',
  },
  NO_TARGET: {
    label: 'No target agreed',
    tone: 'bg-status-warning-subtle text-status-warning-foreground',
    blurb: 'Nothing was promised, so there is nothing to measure against.',
  },
  NOT_MEASURED: {
    label: 'Not measured',
    tone: 'bg-muted text-muted-foreground',
    blurb: 'No day of position has been recorded yet, so this cannot be answered.',
  },
};

const share = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

export function SlaPanel({ rows, onChanged }: { rows: SlaCompliance[]; onChanged: () => void }) {
  const [editing, setEditing] = useState<SlaCompliance | null>(null);

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">No customer contracts</CardTitle>
          <CardDescription>
            A contract appears here once a company is linked as a customer partner. Targets are
            recorded against that relationship.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {rows.map((row) => {
        const v = VERDICT[row.verdict];
        return (
          <Card key={row.customerId}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="text-base">{row.customer}</CardTitle>
                  <CardDescription className="mt-1">
                    {v.blurb}
                    {row.contractReference && (
                      <span className="ml-1 font-mono text-xs">({row.contractReference})</span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn('font-normal', v.tone)}>{v.label}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                    {row.target == null ? 'Set target' : 'Change target'}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Agreed
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">{share(row.target)}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.targetSetBy
                      ? `by ${row.targetSetBy}${row.targetSetAt ? ` on ${new Date(row.targetSetAt).toLocaleDateString()}` : ''}`
                      : 'nobody has agreed one'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Actual
                  </p>
                  <p
                    className={cn(
                      'text-2xl font-semibold tabular-nums',
                      row.verdict === 'MISSED' && 'text-status-destructive-foreground',
                      row.verdict === 'MET' && 'text-status-success-foreground'
                    )}
                  >
                    {share(row.availability)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {row.skuDays > 0 ? `${row.skuDays} item-days` : 'nothing recorded'}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Measured over
                  </p>
                  <p className="text-2xl font-semibold tabular-nums">{row.daysObserved}</p>
                  <p className="text-xs text-muted-foreground">
                    {/* Days recorded, not days elapsed — a gap in the history is a
                        gap in the claim, and saying so is the difference between
                        a measurement and an assumption. */}
                    days recorded of the {row.daysRequested} asked for
                  </p>
                </div>
              </div>

              {row.targetNote && (
                <p className="border-l-2 pl-3 text-xs text-muted-foreground">{row.targetNote}</p>
              )}

              {row.offenders.length > 0 && (
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    What is dragging it down
                  </p>
                  <ul className="space-y-1">
                    {row.offenders.map((o) => (
                      <li key={o.sku} className="flex items-baseline justify-between gap-3 text-sm">
                        <span>
                          <span className="font-mono text-xs">{o.sku}</span>{' '}
                          <span className="text-muted-foreground">{o.name}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-status-destructive-foreground">
                          {share(o.availability)}{' '}
                          <span className="text-xs text-muted-foreground">
                            ({o.met}/{o.total} days)
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {row.verdict === 'NOT_MEASURED' && (
                <p className="text-xs text-status-warning-foreground">
                  Positions are recorded once a day. If this stays empty the scheduler is not
                  running, which is a different problem from missing a target and would otherwise
                  look identical to meeting one.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}

      <TargetDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          onChanged();
        }}
      />
    </div>
  );
}

/**
 * Recording a target.
 *
 * Entered as a percentage because that is how a contract is written, and stored
 * as a share because that is what it is. The conversion happens once, here, so
 * nothing downstream has to guess which it is looking at.
 */
function TargetDialog({
  row,
  onClose,
  onSaved,
}: {
  row: SlaCompliance | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setPercent(row.target == null ? '' : String(row.target * 100));
    setNote(row.targetNote ?? '');
  }, [row]);

  if (!row) return null;

  const value = percent.trim() === '' ? null : Number(percent);
  const problems: string[] = [];
  if (value != null && (Number.isNaN(value) || value <= 0 || value > 100)) {
    problems.push('A target is a percentage above 0 and at most 100.');
  }

  const save = async () => {
    setSaving(true);
    try {
      await consignmentApi.setContractTargets({
        customerCompanyId: row.customerId,
        availabilityTarget: value == null ? null : value / 100,
        note: note.trim() || undefined,
      });
      toast.success(value == null ? 'Target cleared' : `Target set at ${value}%`);
      await onSaved();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not record that target';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Availability target — {row.customer}</DialogTitle>
          <DialogDescription>
            The share of days the items at this customer&rsquo;s sites should stand at or above the
            minimum agreed for them. Recorded against your name, because it is a promise rather than
            a setting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sla-target">Target</Label>
            <div className="flex items-center gap-2">
              <Input
                id="sla-target"
                type="number"
                min={1}
                max={100}
                step="0.1"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                placeholder="none"
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sla-note">Where it comes from</Label>
            <Textarea
              id="sla-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="The clause, or the argument behind the number."
            />
          </div>

          {value == null && (
            <p className="text-xs text-status-warning-foreground">
              Leaving it blank records that no target is agreed. Compliance stops being reported
              for this customer, which is different from recording a target of zero.
            </p>
          )}

          {problems.map((p) => (
            <p key={p} className="text-xs text-status-destructive-foreground">
              {p}
            </p>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || problems.length > 0}>
            {saving ? 'Saving…' : value == null ? 'Record as not agreed' : 'Record target'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
